import * as React from 'react';
import { useRef, useState, useEffect } from 'react';
import { cn } from '@/lib/cn.js';
import { Calendar } from 'lucide-react';

export const Input = React.forwardRef(function Input({ className, type = 'text', ...props }, ref) {
  return (
    <input
      ref={ref}
      type={type}
      className={cn(
        'flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100',
        className,
      )}
      {...props}
    />
  );
});

export const Textarea = React.forwardRef(function Textarea({ className, rows = 3, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(
        'flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100',
        className,
      )}
      {...props}
    />
  );
});

export function Label({ className, children, htmlFor, required, ...props }) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn('text-sm font-medium text-slate-700 dark:text-slate-300', className)}
      {...props}
    >
      {children}
      {required && <span className="ml-0.5 text-red-500">*</span>}
    </label>
  );
}

export function FormField({ label, htmlFor, required, error, hint, children, className }) {
  return (
    <div className={cn('space-y-1', className)}>
      {label && (
        <Label htmlFor={htmlFor} required={required}>
          {label}
        </Label>
      )}
      {children}
      {hint && !error && <p className="text-xs text-slate-500">{hint}</p>}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

// Displays dd/MM/yyyy; value prop and onChange emit yyyy-MM-dd
export function DateInput({ value = '', onChange, className, disabled, ...props }) {
  const pickerRef = useRef(null);

  const toDisplay = (iso) => (iso ? iso.split('-').reverse().join('/') : '');
  const toISO = (display) => {
    const parts = display.split('/');
    if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return '';
  };

  const [text, setText] = useState(toDisplay(value));
  useEffect(() => { setText(toDisplay(value)); }, [value]);

  const handleText = (e) => {
    const raw = e.target.value;
    setText(raw);
    const iso = toISO(raw);
    if (iso) onChange?.({ target: { value: iso } });
  };

  const handlePicker = (e) => {
    onChange?.(e);
    setText(toDisplay(e.target.value));
  };

  return (
    <div className="relative">
      <input
        type="text"
        placeholder="dd/mm/yyyy"
        value={text}
        onChange={handleText}
        disabled={disabled}
        className={cn(
          'flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-1 pr-8 text-sm shadow-sm transition-colors placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100',
          className,
        )}
        {...props}
      />
      <input
        ref={pickerRef}
        type="date"
        value={value}
        onChange={handlePicker}
        disabled={disabled}
        tabIndex={-1}
        className="sr-only"
      />
      <button
        type="button"
        disabled={disabled}
        onClick={() => pickerRef.current?.showPicker?.()}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 disabled:opacity-50"
      >
        <Calendar className="h-4 w-4" />
      </button>
    </div>
  );
}
